/**
 * Olea refrigeration bays
 *
 * The pod has three refrigeration bays. Bay 1 is fixed and lives in the
 * standard inclusions. Bays 2 and 3 each offer a mutually exclusive choice of
 * appliance, or "leave as storage".
 *
 * Each bay is a radio group, so exactly one input per bay is ever checked and a
 * new selection replaces the previous one in the total rather than stacking —
 * the summing below needs no special-casing to get that right. The "leave as
 * storage" option carries data-price="0" and no variant id, so it contributes
 * nothing to the total and nothing to the cart.
 *
 * This does two jobs:
 *
 *   1. Live total — updates a displayed total as add-ons are selected, before
 *      anything is added to the cart.
 *   2. Cart — adds the selected add-ons alongside the pod, as their own line
 *      items, on a single add-to-cart click.
 *
 * Design note: this deliberately does NOT hook Horizon's internal cart events.
 * Those come from the external @shopify/events module, and building on that
 * contract would break on a theme update. Instead it intercepts the add-to-cart
 * click, posts the add-ons itself, then replays the click so Horizon's own
 * add-to-cart logic runs untouched. If the add-on POST fails, the pod is still
 * added — the failure is logged and never blocks the sale.
 */

class OleaAddons extends HTMLElement {
  connectedCallback() {
    this.form = this.closest('form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]');

    // Matches radios and checkboxes alike; bays use radios.
    this.inputs = Array.from(this.querySelectorAll('[data-olea-addon]'));
    this.totalEl = this.querySelector('[data-olea-total]');
    this.extrasEl = this.querySelector('[data-olea-extras]');
    this.currency = this.dataset.currency || 'AUD';

    try {
      const node = this.querySelector('[data-olea-variant-prices]');
      this.variantPrices = node ? JSON.parse(node.textContent) : {};
    } catch (e) {
      this.variantPrices = {};
    }

    this.basePrice = Number(this.dataset.basePrice || 0);

    this.onChange = this.onChange.bind(this);
    this.onClickCapture = this.onClickCapture.bind(this);

    this.inputs.forEach((el) => el.addEventListener('change', this.onChange));

    // Variant switches update the base price. Horizon rewrites the hidden id
    // input when the finish changes; listening for bubbled change/input on the
    // form catches that without depending on any theme-internal event name.
    if (this.form) {
      this.form.addEventListener('change', this.onChange);
      this.form.addEventListener('input', this.onChange);
      this.form.addEventListener('click', this.onClickCapture, true);
    }

    this.render();
  }

  disconnectedCallback() {
    this.inputs.forEach((el) => el.removeEventListener('change', this.onChange));
    if (this.form) {
      this.form.removeEventListener('change', this.onChange);
      this.form.removeEventListener('input', this.onChange);
      this.form.removeEventListener('click', this.onClickCapture, true);
    }
  }

  /** Current base price in cents, following the selected variant. */
  currentBase() {
    const input = this.form && this.form.querySelector('input[name="id"], select[name="id"]');
    if (input && this.variantPrices[input.value] != null) {
      return Number(this.variantPrices[input.value]);
    }
    return this.basePrice;
  }

  selected() {
    return this.inputs.filter((el) => el.checked);
  }

  onChange() {
    this.render();
  }

  format(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  render() {
    const chosen = this.selected();
    const extras = chosen.reduce((sum, cb) => sum + Number(cb.dataset.price || 0), 0);
    const base = this.currentBase();

    if (this.extrasEl) {
      this.extrasEl.textContent = extras > 0 ? '+ ' + this.format(extras) : '';
      this.extrasEl.hidden = extras === 0;
    }
    if (this.totalEl) {
      this.totalEl.textContent = this.format(base + extras);
    }
    this.dataset.selectedCount = String(chosen.length);
    this.renderMap(chosen);
  }

  /** Reflect each bay's choice on the front-elevation diagram. */
  renderMap(chosen) {
    this.querySelectorAll('[data-olea-bay]').forEach((bay) => {
      const key = bay.dataset.oleaBay;
      const pick = chosen.find((el) => bay.contains(el));
      const filled = Boolean(pick && pick.dataset.variantId);
      const slot = this.querySelector(`[data-olea-bay-slot="${key}"]`);
      if (slot) slot.classList.toggle('is-filled', filled);
      const caption = this.querySelector(`[data-olea-bay-caption="${key}"] [data-olea-bay-choice]`);
      if (caption) caption.textContent = pick ? pick.dataset.short || '' : 'Storage';
    });
  }

  /**
   * Intercept the add-to-cart click, add the selected add-ons first, then
   * replay the click so Horizon adds the pod itself.
   */
  onClickCapture(event) {
    const button = event.target.closest('[data-testid="standalone-add-to-cart"], .add-to-cart-button, button[type="submit"]');
    if (!button || this.replaying) return;

    const chosen = this.selected().filter((cb) => cb.dataset.variantId);
    if (chosen.length === 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const items = chosen.map((cb) => ({
      id: Number(cb.dataset.variantId),
      quantity: 1,
    }));

    const replay = () => {
      this.replaying = true;
      button.click();
      // Released on a timeout rather than synchronously, so the replayed click
      // has finished dispatching before another can be intercepted.
      setTimeout(() => { this.replaying = false; }, 0);
    };

    // Theme is declared with `const` in a classic script, so it is a global
    // lexical binding rather than a property of window — reachable by bare
    // reference from a module, but not as window.Theme.
    let addUrl = '/cart/add.js';
    try {
      if (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_add_url) {
        addUrl = Theme.routes.cart_add_url;
      }
    } catch (e) { /* fall back to the default route */ }

    fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    })
      .then((r) => r.json())
      .then(() => replay())
      .catch((err) => {
        // Never block the sale on an add-on failure.
        console.error('[olea-addons] add-ons could not be added to the cart', err);
        replay();
      });
  }
}

if (!customElements.get('olea-addons')) {
  customElements.define('olea-addons', OleaAddons);
}
